import React from "react";
import { Link } from "react-router-dom";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
// The access type labels live with the other carrier helpers, so the
// Extended PoP views can use them without importing this module (which
// imports theirs).
import {
  ACCESS_TYPE_OPTIONS,
  accessTypeLabel,
  formatCarrierAccessNames,
} from "kontrolEdge/helpers/carrierAccess";
import { ResourceTagsTableCell } from "../Shared/ResourceTagsTableCell";
import { endpointDetailPath } from "./CarrierAccessDetails/carrierAccessLegs";
// Same lifecycle values as Extended PoP (draft | provisioning | active |
// suspended | decommissioned), so the pill and its sort order are shared
// rather than copied.
import { extenderPopStatusPresentation as statusPresentation } from "../ExtenderPop/ExtenderPopData";
import type {
  CarrierAccessEndpoint,
  CarrierAccessItem,
} from "./carrierAccess.types";

/**
 * Carrier Access — list table columns. Kept separate from the view per the
 * <Service>Data.tsx convention (see Backbone/Port/Reporting).
 */

export { ACCESS_TYPE_OPTIONS, accessTypeLabel };

export { statusPresentation as carrierAccessStatusPresentation };

const PARENT_CONNECTION_PREFIX: Record<CarrierAccessEndpoint["type"], string> =
  {
    port: "[PORT]",
    extender_pop: "[EXTENDED POP]",
  };

/**
 * The service an access hangs off, as a "Parent Connection" — the label and
 * the `[PORT]10-7E2C9A` form Cloud Connectivity and DC Interconnect already
 * use for theirs, so Carrier Access reads the same (Jose, PR #696).
 */
export const formatParentConnection = (
  endpoint: CarrierAccessEndpoint
): string =>
  `${PARENT_CONNECTION_PREFIX[endpoint.type]}${endpoint.serviceId ?? endpoint.name ?? endpoint.id}`;

/** One parent connection, linking to that Port's or Extended PoP's detail. */
export const ParentConnectionLink: React.FC<{
  endpoint: CarrierAccessEndpoint;
}> = ({ endpoint }) => (
  <Link
    to={endpointDetailPath(endpoint)}
    onClick={(event) => event.stopPropagation()}
    className="whitespace-nowrap font-medium underline text-semantic-text-brand hover:underline"
  >
    {formatParentConnection(endpoint)}
  </Link>
);

const ParentConnectionCell: React.FC<{ row: CarrierAccessItem }> = ({
  row,
}) => {
  if (row.endpoints.length === 0) return <div>-</div>;
  return (
    <div className="flex flex-col leading-tight">
      {row.endpoints.map((endpoint) => (
        <ParentConnectionLink
          key={`${endpoint.type}:${endpoint.id}`}
          endpoint={endpoint}
        />
      ))}
    </div>
  );
};

export const carrierAccessColumns: GenericTableColumn<CarrierAccessItem>[] = [
  {
    header: "Carrier Access ID",
    accessor: "serviceId",
  },
  {
    header: "Name",
    accessor: "name",
  },
  {
    header: "Tags",
    renderCell: (row) => (
      <ResourceTagsTableCell
        tagsRich={row.tagsRich}
        maxVisible={1}
        triggerSingleRow
      />
    ),
  },
  {
    header: "Type",
    renderCell: (row) =>
      row.accessType ? (
        <div>{accessTypeLabel(row.accessType)}</div>
      ) : (
        <span className="text-semantic-text-secondary">Not set</span>
      ),
    sortAccessor: (row) => accessTypeLabel(row.accessType),
  },
  {
    // `formatCarrierAccessNames` collapses one carrier reached through two
    // markers (`lm` and `carr`) — two provenances, not two carriers.
    header: "Carriers",
    renderCell: (row) => (
      <div>{formatCarrierAccessNames(row.carrierAccess) || "-"}</div>
    ),
    sortAccessor: (row) => formatCarrierAccessNames(row.carrierAccess),
  },
  {
    header: "Parent Connection",
    renderCell: (row) => <ParentConnectionCell row={row} />,
    sortAccessor: (row) => row.endpoints.map(formatParentConnection).join(" "),
  },
  {
    header: "Status",
    renderCell: (row) => {
      const { variant, label } = statusPresentation(row.status);
      return <GenericTag variant={variant} label={label} size="small" />;
    },
    sortAccessor: (row) => statusPresentation(row.status).rank,
  },
  {
    header: "Term",
    renderCell: (row) => <div>{row.term}</div>,
  },
  {
    header: "Renew Date",
    accessor: "renewDate",
  },
];
